import { useQuery } from "@tanstack/react-query";
import {
  fetchEntitySchema,
  fetchOptionUsage,
  fetchSchemaFill,
  type OptionUsage,
  fetchSchemaSync,
  type SchemaFill,
  type SchemaSync,
} from "../Dashboard/api";
import type { EntityKind, RmsSchema } from "../Dashboard/types";
import { useRmsBootstrap } from "./bootstrap";
import {
  keyForSource,
  useRecordDataSource,
} from "../Dashboard/recordDataSource";
import { DOCUMENT_REVIEW_SCHEMA } from "./documentReviewSchema";

// Schema kinds the frontend owns outright: fixed, org-agnostic column layouts
// that neither the bootstrap nor /rms/schema/{kind} serves. Resolved from a
// constant through a synchronous queryFn below, so callers still get a real
// UseQueryResult and no network request is issued.
const BUILT_IN_SCHEMAS: Record<string, RmsSchema> = {
  document_review: DOCUMENT_REVIEW_SCHEMA,
};

// Query Keys
export const SCHEMA_QUERY_KEYS = {
  // `scopePublicId` is part of the key: a dataset's scoped schema names a
  // fixed column the unscoped one does not, and the two must not share a cache
  // entry.
  entitySchema: (
    schemaKind: string,
    organizationId?: string,
    flat?: boolean,
    scopePublicId?: string
  ) =>
    ["rms", "schema", schemaKind, organizationId, flat, scopePublicId] as const,
};

// Schema hook. When called with the bootstrap defaults (current user's org,
// non-flat), this reads the schema slice out of the shared RMS bootstrap
// cache — no extra network request. The organizationId/flat path is reserved
// for admin tooling (e.g. editing ADMIN_ORG templates) which still needs a
// dedicated fetch.
//
export const useEntitySchema = (
  // Null names no schema and asks for nothing (a mixed dashboard has no kind of its own).
  schemaKind: string | null,
  organizationId?: string,
  flat?: boolean,
  // Only a dataset's record tab passes one (#12178): its fixed column depends
  // on which record, so that schema is asked for per record and never read
  // from the bootstrap.
  scopePublicId?: string
) => {
  const named = schemaKind !== null;
  // Read the bootstrap whatever is asked (hooks run in one order); no kind reads nothing.
  const useBootstrap = named && !organizationId && !flat && !scopePublicId;

  // Document review is a fixed, org-agnostic layout the frontend owns — the backend bootstrap / schema endpoint don't
  // serve them. Resolve from the built-in constant via a synchronous queryFn
  // so the returned object is a real UseQueryResult (callers read .data /
  // .error / .isLoading / .isError). No network request is issued.
  const kind = schemaKind ?? "";
  const builtInSchema: RmsSchema | undefined = named
    ? BUILT_IN_SCHEMAS[kind]
    : undefined;
  const isBuiltIn = builtInSchema !== undefined;

  // A built-in kind never reads the bootstrap, so it does not ask for it.
  const bootstrapQuery = useRmsBootstrap<RmsSchema | undefined>({
    // Per-client column labels arrive resolved as `display_name` from the
    // backend (#10972) — nothing to apply here.
    select: (data) =>
      schemaKind === null ? undefined : data.schemas[schemaKind as EntityKind],
    enabled: useBootstrap && !isBuiltIn,
  });

  // The bootstrap serves the record kinds, not every schema kind a saved
  // view can name (a shadowbook aggregation, for one). A kind it turns out
  // not to hold must fall through to the dedicated endpoint rather than
  // "succeed" as undefined — that shape left the grid's skeleton on screen
  // forever, with no error for the page to report (#11426).
  const bootstrapMiss =
    useBootstrap && bootstrapQuery.isSuccess && bootstrapQuery.data == null;

  // A schema read for one record is that record's data: it goes through the record's data
  // source, so Appearance's demo record never reaches the API (#13370, round 36 review).
  const source = useRecordDataSource();
  const baseKey = SCHEMA_QUERY_KEYS.entitySchema(
    kind,
    organizationId,
    flat,
    scopePublicId
  );
  const fallbackQuery = useQuery({
    queryKey: scopePublicId ? keyForSource(source, baseKey) : baseKey,
    queryFn: (): Promise<RmsSchema | undefined> =>
      builtInSchema !== undefined
        ? Promise.resolve(builtInSchema)
        : scopePublicId
          ? source.recordSchema(kind, scopePublicId, organizationId, flat)
          : fetchEntitySchema(kind, organizationId, flat),
    // A response that is not a schema is NOT a schema: hand back undefined
    // rather than an object whose `.facts` is missing. Consumers already
    // guard `!schema` and then read `schema.facts` — before the
    // bootstrap-miss fallthrough this path never ran for a bootstrap-served
    // kind, so a malformed body could not reach them; now it can, and the
    // hook is the one place that can stop it once (#11426).
    select: (data): RmsSchema | undefined =>
      data && Array.isArray((data as RmsSchema).facts)
        ? (data as RmsSchema)
        : undefined,
    enabled: named && (!useBootstrap || isBuiltIn || bootstrapMiss),
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return useBootstrap && !isBuiltIn && !bootstrapMiss
    ? bootstrapQuery
    : fallbackQuery;
};

// Which attributes a connector syncs, and how (#12228). Editor-only, like the
// fill counts below, so it is fetched here rather than riding the bootstrap
// every RMS page loads. On failure the editor simply shows no sync details.
export const useSchemaSync = (entityKind: string, organizationId?: string) => {
  return useQuery<SchemaSync, Error, SchemaSync | undefined>({
    queryKey: ["rms", "schema-sync", entityKind, organizationId],
    queryFn: () => fetchSchemaSync(entityKind, organizationId),
    // A body that is not a sync answer is no answer, the same guard
    // `useEntitySchema` puts on a schema: the editor reads `.attributes` on
    // every row, so a wrong shape here would take the whole list down.
    select: (data): SchemaSync | undefined =>
      data &&
      typeof data.attributes === "object" &&
      data.attributes !== null &&
      !Array.isArray(data.attributes)
        ? data
        : undefined,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
};

// Per-attribute fill counts for the record-model attribute list's fill bars
// (#11426 gaps-v9). Only the entity kinds have a fill endpoint — the editor
// is the sole consumer, so no bootstrap slice exists. On failure the query
// reports an error and the list stands the bars down (CD ruling: an
// uncountable bar disappears; it never renders an empty track).
export const useSchemaFill = (entityKind: string, organizationId?: string) => {
  return useQuery<SchemaFill>({
    queryKey: ["rms", "schema-fill", entityKind, organizationId],
    queryFn: () => fetchSchemaFill(entityKind, organizationId),
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
};

// The values one attribute's records hold (#12229), for the field editor's
// merge line and a new dropdown's first options. Under the "rms" key, so a
// schema save (which invalidates every "rms" query) refetches it.
export const useOptionUsage = (
  entityKind: string | undefined,
  attributeId: string | undefined,
  enabled: boolean,
  organizationId?: string
) => {
  return useQuery<OptionUsage>({
    queryKey: ["rms", "option-usage", entityKind, attributeId, organizationId],
    queryFn: () => fetchOptionUsage(entityKind!, attributeId!, organizationId),
    enabled: enabled && !!entityKind && !!attributeId,
    staleTime: 60 * 1000,
    retry: 1,
  });
};
